import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, CheckCircle } from 'lucide-react';
import Button from './button';

interface ExclusiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactClick: () => void;
}

export default function ExclusiveModal({ isOpen, onClose, onContactClick }: ExclusiveModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {/* Header avec gradient */}
            <div className="bg-gradient-to-r from-[#d03484] to-[#d47254] p-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
              
              <div className="flex items-center gap-3 mb-2">
                <Shield size={32} className="text-white" />
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                  EXCLUSIVITÉ
                </span>
              </div>
              
              <h2 className="text-2xl font-bold mb-2">
                Assurance Période d'Essai
              </h2>
              <p className="text-white/90 text-lg">
                La seule assurance qui rembourse vos honoraires
              </p>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <CheckCircle size={20} className="text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-neutral-700">
                    <strong>Remboursement</strong> en cas de rupture de période d'essai de vos candidats
                  </p>
                </div>
                
                <div className="flex items-start gap-3 mb-4">
                  <CheckCircle size={20} className="text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-neutral-700">
                    <strong>Protection financière</strong> pour sécuriser vos investissements RH
                  </p>
                </div>
                
                <div className="flex items-start gap-3 mb-6">
                  <CheckCircle size={20} className="text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-neutral-700">
                    <strong>Tranquillité d'esprit</strong> pour vos recrutements stratégiques
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-neutral-700 text-center">
                  <strong>🎯 Offre limitée :</strong> Découvrez comment cette assurance révolutionne 
                  la sécurité de vos recrutements
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="gradient"
                  onClick={onContactClick}
                  className="flex-1"
                >
                  En savoir plus
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                >
                  Plus tard
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}